import type { QueueItem } from "@/lib/review/queue"
import type { StringLocation } from "../../scripts/string-locations-build-core"
import { isPathAllowedInCountry } from "@/lib/country-gates"
import type { Country } from "@/i18n/config"

/**
 * Message-file locale → the site's two-segment `/{country}/{language}` route
 * prefix. The site routes by country + language (NOT by the BCP47 message-file
 * locale), so de-DE renders at /de/de, en-NL at /nl/en, fr at /ch/fr, etc. This
 * is the inverse of getMessageFile() in i18n/config.ts.
 */
export const LOCALE_TO_ROUTE: Record<
  string,
  { country: string; language: string }
> = {
  en: { country: "ch", language: "en" },
  de: { country: "ch", language: "de" },
  fr: { country: "ch", language: "fr" },
  nl: { country: "nl", language: "nl" },
  "en-NL": { country: "nl", language: "en" },
  "de-DE": { country: "de", language: "de" },
  "de-AT": { country: "at", language: "de" },
}

const isHome = (p: string) => p === "" || p === "/"

/** `/{country}/{language}[/{path}]?review=<key>` for one bare path. */
function hrefFor(
  route: { country: string; language: string },
  bare: string,
  key: string
): string {
  const seg = bare === "" || bare === "/" ? "" : `/${bare}`
  return `/${route.country}/${route.language}${seg}?review=${encodeURIComponent(key)}`
}

/**
 * The crawl index orders `paths` by traffic, so `paths[0]` is the busiest page
 * a string renders on — but two things make the raw busiest path the wrong jump
 * target:
 *  - it can be country-gated OFF for the locale under review (e.g. a CH-only
 *    page while reviewing NL), which would 404 the reviewer; and
 *  - it can be the **home** route, even for a string with a dedicated page
 *    (e.g. an `imprint.*` legal string that also surfaces in footer chrome on
 *    home). Reviewing such a string on its own page is far more useful than on
 *    the busy homepage (which in dev may also be the crashing route).
 * So prefer the busiest reachable *non-home* page; only fall back to a reachable
 * home route when that's all the string renders on. Returns null when nothing is
 * reachable — the caller then uses the namespace jumpPath / locale home.
 */
function busiestReachablePath(paths: string[], country: string): string | null {
  const reachable = paths.filter((p) =>
    isPathAllowedInCountry(p, country as Country)
  )
  return reachable.find((p) => !isHome(p)) ?? reachable[0] ?? null
}

/**
 * `/{country}/{language}` route href to the item's page carrying ?review.
 * The crawl index (`location`) wins when present — its paths are the pages the
 * string renders on, traffic-ordered (a path may be "" = home); otherwise the
 * namespace `jumpPath`. With neither, we land on the locale home ("") — nav,
 * footer and legal chrome all render there, and it's at least the correct
 * country+locale, so the reviewer always advances instead of getting stuck.
 * Only an unknown locale (no route) yields null.
 */
export function reviewHref(
  item: Pick<QueueItem, "key" | "jumpPath" | "locale">,
  location: StringLocation | null = null
): string | null {
  const route = LOCALE_TO_ROUTE[item.locale]
  if (!route) return null
  // Precedence: a crawl path reachable in the target country → the namespace
  // jumpPath → the locale home ("").
  const reachable =
    location && location.paths.length > 0
      ? busiestReachablePath(location.paths, route.country)
      : null
  // The namespace jumpPath is the fallback when the crawl index has nothing —
  // but if that page is country-gated out of the target locale, land on the
  // locale home instead of pushing a guaranteed 404. Auto-advance must never
  // dead-end the cockpit on a missing route.
  const jump =
    item.jumpPath && !isHome(item.jumpPath)
      ? isPathAllowedInCountry(item.jumpPath, route.country as Country)
        ? item.jumpPath
        : ""
      : (item.jumpPath ?? "")
  const bare = reachable ?? jump
  return hrefFor(route, bare, item.key)
}

/** One page a shared-UI string renders on: its bare path + the cockpit href. */
export interface ReviewPlace {
  /** Bare path ("" = home), as stored in the crawl index. */
  path: string
  /** `/{country}/{language}[/{path}]?review=<key>` — soft-nav target. */
  href: string
}

/**
 * Every page a string renders on (from the crawl index), as cockpit hrefs —
 * the multi-place counterpart to `reviewHref`'s single best jump. Used to let a
 * reviewer pick *which* page to confirm a shared-UI string on, since a
 * `chromeLike` string isn't tied to one page (AHD-7886). Paths are
 * traffic-ordered and already capped for chrome-like keys (`CHROME_PATH_CAP`);
 * we drop pages country-gated out of the locale (same 404 guard as `reviewHref`)
 * and de-dupe home. Empty when the locale is unknown or the index has no entry.
 */
export function reviewPlaces(
  item: Pick<QueueItem, "key" | "locale">,
  location: StringLocation | null = null
): ReviewPlace[] {
  const route = LOCALE_TO_ROUTE[item.locale]
  if (!route || !location) return []
  const seen = new Set<string>()
  const places: ReviewPlace[] = []
  for (const p of location.paths) {
    if (!isPathAllowedInCountry(p, route.country as Country)) continue
    const bare = isHome(p) ? "" : p
    if (seen.has(bare)) continue
    seen.add(bare)
    places.push({ path: bare, href: hrefFor(route, bare, item.key) })
  }
  return places
}

/** Find the DOM node rendering `value`, scroll to it, flash a highlight. */
export function findAndHighlight(value: string): boolean {
  if (!value || typeof document === "undefined") return false
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT)
  let node: Node | null
  while ((node = walker.nextNode())) {
    if (node.textContent?.trim() === value.trim()) {
      const el = node.parentElement
      if (!el) continue
      el.scrollIntoView({ behavior: "smooth", block: "center" })
      el.classList.add("ahead-review-flash")
      setTimeout(() => el.classList.remove("ahead-review-flash"), 1600)
      return true
    }
  }
  return false
}
